/**
 * Prefetch on intent.
 *
 * A tap is not instant: the browser sees pointerdown, then waits for the
 * finger to lift before dispatching the click, and only then does it start
 * fetching the next document. That gap is dead time — typically 100-200ms on a
 * phone, on top of the server's own response time.
 *
 * This starts the document fetch at pointerdown instead, so by the time the
 * click lands the response is already on its way or in the cache. Nothing is
 * navigated early; a prefetch that goes unused simply costs one request.
 *
 * Deliberately conservative:
 *   - same-origin document links only, no downloads, no new tabs
 *   - one prefetch per URL, capped for the session
 *   - skipped entirely on Save-Data or a slow connection, where speculative
 *     requests cost the visitor money and bandwidth they did not ask to spend
 */
const PREFETCHED = new Set();
const LIMIT = 12;

function shouldSkip() {
  const c = navigator.connection;
  if (!c) return false;
  if (c.saveData) return true;
  return /(^|-)2g$/.test(c.effectiveType || '');
}

function candidate(target) {
  const link = target?.closest?.('a[href]');
  if (!link) return null;

  // Anything that is not a plain in-site navigation is left alone.
  if (link.target && link.target !== '_self') return null;
  if (link.hasAttribute('download')) return null;

  let url;
  try {
    url = new URL(link.href, location.href);
  } catch {
    return null;
  }

  if (url.origin !== location.origin) return null;
  if (!/^https?:$/.test(url.protocol)) return null;
  // Same page, or a jump within it.
  if (url.pathname === location.pathname && url.search === location.search) return null;
  // Cart and account actions are stateful; prefetching them is not free.
  if (/^\/(cart|account|checkout)/.test(url.pathname)) return null;

  return url.href;
}

function prefetch(href) {
  if (!href || PREFETCHED.has(href) || PREFETCHED.size >= LIMIT) return;
  PREFETCHED.add(href);

  const link = document.createElement('link');
  link.rel = 'prefetch';
  link.as = 'document';
  link.href = href;
  document.head.appendChild(link);
}

/**
 * Speculation Rules, where the browser supports them.
 *
 * prefetch only fetches the HTML; the visitor still waits for the page's own
 * CSS, JS and images afterwards — and on this store a product page pulls ~279
 * requests, of which around 187 are Shopify's own checkout prefetch and
 * analytics rather than anything the theme controls. Fetching the document
 * early barely dents that.
 *
 * prerender builds the whole page in the background instead, so the click
 * swaps to a page that is already rendered. That is what "opens instantly"
 * actually requires here.
 *
 * eagerness "moderate" means the browser only does this on a real intent
 * signal — hover held, or pointer down — not for every link on the page, so
 * the cost stays proportional to what the visitor is about to do.
 */
function speculationRules() {
  if (!HTMLScriptElement.supports?.('speculationrules')) return false;

  const script = document.createElement('script');
  script.type = 'speculationrules';
  script.textContent = JSON.stringify({
    prerender: [
      {
        where: {
          and: [
            { href_matches: '/*' },
            // Stateful routes must never be built ahead of time: prerendering
            // a cart or checkout URL can fire side effects before the visitor
            // has actually chosen to go there.
            { not: { href_matches: '/cart*' } },
            { not: { href_matches: '/checkout*' } },
            { not: { href_matches: '/account*' } },
            { not: { selector_matches: '[rel~=nofollow]' } },
            { not: { selector_matches: '[download]' } },
          ],
        },
        eagerness: 'moderate',
      },
    ],
  });
  document.head.appendChild(script);
  return true;
}

if (!shouldSkip()) {
  // When the browser can prerender, that supersedes the manual prefetch below —
  // running both would fetch the same document twice.
  const prerendering = speculationRules();
  if (prerendering) {
    // Nothing else to wire up; the browser handles the intent detection.
  } else {
    // pointerdown covers both: a finger touching down, and a mouse button
    // going down before it comes back up.
    document.addEventListener('pointerdown', (event) => prefetch(candidate(event.target)), {
      passive: true,
      capture: true,
    });

    // On a pointer device the intent is visible even earlier.
    if (window.matchMedia('(hover: hover)').matches) {
      document.addEventListener(
        'pointerover',
        (event) => prefetch(candidate(event.target)),
        { passive: true, capture: true }
      );
    }
  }
}
